import { getPerfHackIsClosed } from '@rover/react-lib/src/components/modals/Modal/Modal.common';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import {
  effects as searchPageEffects,
  selectors as searchPageSelectors,
} from '@rover/react-lib/src/pages/search/SearchPage/SearchPage.duck';
import { selectors as frontendConfigurationSelectors } from '@rover/react-lib/src/redux/ducks/frontendConfiguration.duck';
import { FLAGS } from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import type { SearchFilters } from '@rover/types';

import { selectors as searchFiltersSelectors } from './SearchFilters.duck';

export type State = {
  hoveringResultIndex?: number | null;
  markerZIndices: Record<number, number>;
  isExpanded: boolean;
  showStaticMap: boolean;
  showDynamicMap: boolean;
};

type Actions = {
  resultMarkerHovered: ActionCreator<number>;
  resultMarkerHoveredOut: ActionCreator<void>;
  resultCardHovered: ActionCreator<number>;
  resultCardHoveredOut: ActionCreator<void>;
  toggleIsExpanded: ActionCreator<void>;
  setShowStaticMap: ActionCreator<boolean>;
  setShowDynamicMap: ActionCreator<boolean>;
};

type Effects = {
  updateFiltersFireSearchAndLoadDynamicMap: EffectCreator<Partial<SearchFilters>>;
};

type Selectors = {
  getHoveringResultIndex: SelectorCreator<number | null | undefined>;
  getIsExpanded: SelectorCreator<boolean>;
  getMarkerZIndex: SelectorCreator<(index: number) => number>;
  getShowStaticMap: SelectorCreator<boolean>;
  getShowDynamicMap: SelectorCreator<boolean>;
  getRolloutSignStaticMap: SelectorCreator<boolean>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

// whenever a marker becomes active, we calculate a
// new z-index for it, and we want it to be higher than
// all of the previously assigned z-indices, to make sure
// that this marker remains on top
const getNextZIndex = (currentMarkerZIndices: Record<number, number>) => {
  return Math.max(...Object.values(currentMarkerZIndices), 0) + 1;
};

export const options: Options = {
  name: 'searchMap',
  initialState: {
    hoveringResultIndex: null,
    isExpanded: false,
    markerZIndices: {},
    // NOTE: getShowStaticMap currently only shows false for non-experiment users
    showStaticMap: true,
    showDynamicMap: false,
  },
  actions: {
    resultMarkerHovered: (hoveringResultIndex) => (state) => ({
      ...state,
      hoveringResultIndex,
      markerZIndices: {
        ...state.markerZIndices,
        [hoveringResultIndex]: getNextZIndex(state.markerZIndices),
      },
    }),
    resultMarkerHoveredOut: () => (state) => ({ ...state, hoveringResultIndex: null }),
    resultCardHovered: (hoveringResultIndex) => (state) => ({
      ...state,
      hoveringResultIndex,
      markerZIndices: {
        ...state.markerZIndices,
        [hoveringResultIndex]: getNextZIndex(state.markerZIndices),
      },
    }),
    resultCardHoveredOut: () => (state) => ({ ...state, hoveringResultIndex: null }),
    toggleIsExpanded: () => (state) => ({ ...state, isExpanded: !state.isExpanded }),
    setShowStaticMap: (showStaticMap) => (state) => ({ ...state, showStaticMap }),
    setShowDynamicMap: (showDynamicMap) => (state) => ({ ...state, showDynamicMap }),
  },
  effects: {
    updateFiltersFireSearchAndLoadDynamicMap:
      (filters) => async (dispatch, getState, duckActions) => {
        await dispatch(duckActions.setShowDynamicMap(true));
        await dispatch(searchPageEffects.updateFiltersAndFireSearchOnChange(filters));
        await dispatch(duckActions.setShowStaticMap(false));
      },
  },
  selectors: {
    getRolloutSignStaticMap: (getState, createSelector) =>
      createSelector([frontendConfigurationSelectors.isFlagActive], (isFlagActive) =>
        isFlagActive(FLAGS.ROLLOUT_SIGN_STATIC_MAP)
      ),
    getHoveringResultIndex: (getState, createSelector) =>
      createSelector([getState], (state) => state.hoveringResultIndex),
    getIsExpanded: (getState, createSelector) =>
      createSelector([getState], (state) => state.isExpanded),
    getMarkerZIndex: (getState, createSelector) =>
      createSelector([getState], (state) => (index: number) => state.markerZIndices[index] || 0),
    getShowDynamicMap: (getState, createSelector) =>
      createSelector([getState], (state) => {
        // if modal was closed by perf hack, show the dynamic map
        if (getPerfHackIsClosed()) {
          return true;
        }

        return state.showDynamicMap;
      }),
    getShowStaticMap: (getState, createSelector) =>
      createSelector(
        [
          searchFiltersSelectors.getIsDateConstrained,
          searchPageSelectors.getIsModalDisabled,
          getState,
        ],
        (isDateConstrained, isModalDisabled, state) => {
          // don't show the static map under conditions where the modal isn't displayed
          if (isDateConstrained || isModalDisabled || getPerfHackIsClosed()) {
            return false;
          }

          return state.showStaticMap;
        }
      ),
  },
};
export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
